"use client"

import { useMutation } from "@tanstack/react-query";
import { FileText, Upload } from "lucide-react";
import { FileUploader } from "react-drag-drop-files";
import { processCV } from "../../services/cvUploadService";
import {
  finishProcessing,
  finishUpload,
  setActiveTab,
  setDragActive,
  setExtractedData,
  setFile,
  skipToProfile,
  startProcessing,
  startUpload,
  updateUploadProgress
} from "../../store/features/cvUpload/cvUploadSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

const fileTypes = ["PDF", "DOCX", "DOC"];

export function UploadTab() {
  const dispatch = useAppDispatch();
  const { 
    file, 
    previewUrl,
    dragActive,
    uploadStatus: { isUploading, uploadProgress, isProcessing },
  } = useAppSelector((state) => state.cvUpload);

  // Set up React Query mutation for processing CV
  const processCVMutation = useMutation({
    mutationFn: processCV,
    onSuccess: (result) => {
      if (result.success && result.data) {
        dispatch(setExtractedData(result.data));
        dispatch(setActiveTab("profile"));
      } else {
        // Handle error
        console.error("Failed to process CV:", result.error);
        // Here we would show an error message to the user
      }

      dispatch(finishProcessing());
    },
    onError: (error) => {
      console.error("Error processing CV:", error);
      dispatch(finishProcessing());
      // Here we would show an error message to the user
    },
  });

  const handleChange = (file: File) => {
    dispatch(setFile(file));
  };

  const simulateUpload = () => {
    if (!file) return;

    dispatch(startUpload());

    // Simulate upload progress
    const interval = setInterval(() => {
      const newProgress = uploadProgress + 10;
      if (newProgress >= 100) {
        clearInterval(interval);
        dispatch(finishUpload());
        simulateProcessing();
      } else {
        dispatch(updateUploadProgress(newProgress));
      }
    }, 300);
  };

  const simulateProcessing = () => {
    dispatch(startProcessing());

    // In a real app, we would pass the file to an API endpoint
    processCVMutation.mutate(file!);
  };

  return (
    <div className="space-y-3">

      <div className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 space-y-3">
        {!file ? (
          <>
            <div className="p-3 rounded-full bg-primary/10">
              <Upload className="h-6 w-6 text-primary" />
            </div>
            <div className="text-center">
              <h3 className="font-medium text-sm">Drag & Drop CV File (Demo)</h3>
              <p className="text-xs text-muted-foreground">Support for PDF, DOC, DOCX files</p>
            </div>
            <div className="w-full">
              <FileUploader
                handleChange={handleChange}
                name="file"
                types={fileTypes}
                hoverTitle="Drop CV here"
                maxSize={10}
                onSizeError={() => alert("File is too large! Max size is 10MB")}
                onTypeError={() => alert("Invalid file type! Only PDF, DOC, DOCX files are supported")}
                onDraggingStateChange={(dragging: boolean) => dispatch(setDragActive(dragging))}
                classes={`file-uploader ${dragActive ? 'drag-active' : ''}`}
              >
                <div className={`file-uploader-wrapper border-2 border-dashed rounded-lg p-4 transition-all ${dragActive
                    ? 'bg-primary/10 border-primary scale-105'
                    : 'border-input hover:bg-accent/50 hover:border-primary/50'
                  }`}>
                  <div className="flex flex-col items-center justify-center gap-1">
                    {dragActive ? (
                      <>
                        <div className="p-2 rounded-full bg-primary/20">
                          <Upload className="h-5 w-5 text-primary animate-bounce" />
                        </div>
                        <p className="text-xs font-medium text-primary">Drop your file here</p>
                      </>
                    ) : (
                      <Button variant="outline" size="sm" className="text-xs">
                        Browse Files
                      </Button>
                    )}
                  </div>
                </div>
              </FileUploader>
            </div>
          </>
        ) :
          <div className="w-full space-y-3">
            <div className="flex items-center space-x-3">
              <div className="p-1.5 rounded-full bg-primary/10">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">{file.name}</p>
                <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => dispatch(setFile(null))} className="text-xs h-7 px-2">
                Remove
              </Button>
            </div>

            {isUploading && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-1.5" />
              </div>
            )}

            {isProcessing && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span>Processing with AI OCR...</span>
                </div>
                <Progress value={undefined} className="h-1.5" />
              </div>
            )}

            {!isUploading && !isProcessing && (
              <Button onClick={simulateUpload} className="w-full text-xs h-8">
                Process CV
              </Button>
            )}
          </div>
        }
      </div>

      {file && !previewUrl && (
        <div className="mt-3">
          <h3 className="font-medium text-sm mb-1">File Details</h3>
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-full bg-primary/10">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-sm">{file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {file.type || "Unknown file type"} • {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {previewUrl && (
        <div className="mt-3">
          <h3 className="font-medium text-sm mb-1">PDF Preview</h3>
          <div className="file-preview">
            <iframe src={previewUrl} title="CV Preview" />
          </div>
        </div>
      )}

      <div className="flex justify-end mt-6">
        <Button 
          variant="outline" 
          onClick={() => dispatch(skipToProfile())}
        >
          Skip to Profile Information
        </Button>
      </div>
    </div>
  );
} 